import { createContext, useContext } from 'react';

import type { ToggleAuthConnectionOptions } from './hooks/useAuthConnections';
import type { AuthConnection, Region } from './hooks/useCreateAccount';
import type { EmailVerificationStatus } from './hooks/useEmailVerification';
import type { AccountDetailsState } from './signupUtils';

export type SignupScreen =
  | 'loading'
  | 'verify_email'
  | 'under_review'
  | 'workspace_setup'
  | 'login_verify_email'
  | 'invite_email_mismatch'
  | 'welcome';

type AccountDetailsContextValue = {
  accountDetails: AccountDetailsState;
  setField: (data: Partial<AccountDetailsState>) => void;
  handleRegionChange: (region: Region) => void;
};

type AuthConnectionsContextValue = {
  toggleAuthConnection: (options: ToggleAuthConnectionOptions) => void;
  hasSelectedAuthConnection: boolean;
  showAuthConnections: boolean;
  userAuthConnection: AuthConnection;
};

type WorkspaceNameFieldsContextValue = {
  handleDisplayNameChange: (value: string) => void;
  handleDisplayNameBlur: () => void;
  validDisplayName: boolean;
  helperText: string;
};

type CreateAccountFormContextValue = {
  screen: SignupScreen;
  error?: Error;
  showOverlay: boolean;
  canSubmit: boolean;
  currentUserEmail?: string;
  inviteEmail?: string;
  emailVerificationStatus: EmailVerificationStatus;
  acceptMarketingEmails: boolean;
  setAcceptMarketingEmails: (value: boolean) => void;
  handleLogin: (opts?: HandleLoginOptions) => Promise<void>;
  handleLogout: () => Promise<void>;
  handleSelfServeSignup: () => Promise<void>;
  handleCreate: () => Promise<void>;
  handleResultDismissed: () => void;
  recheckEmailVerification: () => Promise<void>;
};

export type HandleLoginOptions = {
  trigger?: 'interaction' | 'automatic';
  silent?: boolean;
};

type CreateAccountFormProviderValue = AccountDetailsContextValue &
  AuthConnectionsContextValue &
  WorkspaceNameFieldsContextValue &
  CreateAccountFormContextValue;

export const CreateAccountFormContext =
  createContext<CreateAccountFormProviderValue | null>(null);

const useFormContext = (): CreateAccountFormProviderValue => {
  const ctx = useContext(CreateAccountFormContext);
  if (!ctx) {
    throw new Error(
      'CreateAccountForm context hooks must be used within CreateAccountFormProvider',
    );
  }
  return ctx;
};

export const useAccountDetailsContext = (): AccountDetailsContextValue =>
  useFormContext();

export const useAuthConnectionsContext = (): AuthConnectionsContextValue =>
  useFormContext();

export const useWorkspaceNameFieldsContext =
  (): WorkspaceNameFieldsContextValue => useFormContext();

export const useCreateAccountFormContext = (): CreateAccountFormContextValue =>
  useFormContext();
